import Link from "next/link";
import { Stack } from "@mui/material";

import { SSText } from "src/components/base/components/SSText";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question:
      "How do I claim my tickets? When does the opt in open before each game?",
    answer:
      "You will receive text when the window to opt-in opens. The opt-in window starts at 9 am on the first game day of a home series. You will have an opportunity to opt-in for all the games in the series and purchase guest tickets as well. The opt-in window closes 3 hours prior to game start.",
  },
  {
    question:
      "Do I get a ticket to each game on the schedule? Are there penalties if I do not opt in?",
    answer:
      "Tickets are guaranteed for each game on the schedule. There are no penalties for not attending a game, however you do need to confirm your attendance in order to receive a ticket.",
  },
  {
    question: "Where are my tickets?",
    answer:
      "Your tickets will be sent over 3 hours prior to game start and you can find the tickets within your Ballpark App.",
  },
  {
    question: "How are tickets sent to me?",
    answer:
      "3 hours prior to game start you will receive a text confirming that tickets have been delivered to your Ballpark App account.",
  },
  {
    question: "Where will my seats be located?",
    answer: "Seat locations will vary by game.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer:
      "Summer Pass tickets, as well as seat upgrades and guest passes, are not transferable and not eligible for resale. Abusing these guidelines can result in your Summer Pass being revoked.",
  },
  {
    question: "Can my seats be changed?",
    answer: (
      <>
        Once tickets have been sent to your account, they cannot be changed.
        Please contact{" "}
        <Link href="mailto:tickets@dbacks.com">tickets@dbacks.com</Link> for any
        assistance you may require.
      </>
    ),
  },
  {
    question: "Can I link tickets with other pass holders?",
    answer:
      "You can link your pass with other pass holders using Sit With a Friend located within the portal on the left navigation. This allows you to create and join ticket groups. All fans within a ticket group will be assigned seats next to each other.",
  },
  {
    question: "Can I bring a friend to a game?",
    answer:
      "Guest tickets will be available for select games. If available, guest tickets are available for purchase when you opt into games.",
  },
  {
    question: "Can I receive a refund if I cannot attend a game?",
    answer: "We do not issue any refunds for games you are not able to attend.",
  },
  {
    question: "How can I get a refund and cancel my pass?",
    answer: (
      <>
        Please contact{" "}
        <Link href="mailto:tickets@dbacks.com">tickets@dbacks.com</Link> for any
        requests for refunds or cancellations.
      </>
    ),
  },
];

export const TERMS_AND_CONDITION = () => {
  return (
    <>
      In connection with your purchase, you will receive ticket-related
      commercial emails from the Arizona Diamondbacks. You may unsubscribe at
      any time. By checking this box you agree to receive additional commercial
      emails from the Arizona Diamondbacks and MLB. The terms and conditions of
      your ticket and policies applicable to its use are updated to address
      circumstances related to COVID-19 and health and safety. By clicking the
      Purchase Tickets button, you agree to accept all{" "}
      <Link
        href={`${window.location.origin}/azdbacks_pass/dbacks_terms_and_condition`}
        target="_blank"
      >
        Terms and Conditions
      </Link>
      . Further, due to the COVID-19 pandemic, scheduling may be impacted; games
      may be canceled, rescheduled, or postponed or games may be held with
      limited capacity. Upon completing your purchase by selecting Purchase
      Tickets you acknowledge and agree that your credit card will be charged.
      By clicking “Purchase Tickets”, you agree to the{" "}
      <Link
        href={
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy"
        }
        target="_blank"
      >
        MLB Privacy Policy
      </Link>
      ,{" "}
      <Link
        href={
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use"
        }
        target="_blank"
      >
        Terms of Use
      </Link>{" "}
      and to receive your ticket(s) via an SMS text message to the mobile #
      associated with your Diamondbacks account. Message and data rates may
      apply Tickets will be made available in the MLB Ballpark mobile
      application only. MLB and supported mobile device required.
    </>
  );
};

export const NavSubmenuLinks = () => {
  const theme = useSSTheme();

  const links = [
    {
      id: 1,
      label: "Arizona Diamondbacks Terms and Conditions",
      onClick: () => {
        window.open(
          `${window.location.origin}/azdbacks_pass/dbacks_terms_and_condition`,
          "_self"
        );
      },
    },
    {
      id: 2,
      label: "MLB Privacy Policy",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          "_self"
        );
      },
    },
    {
      id: 3,
      label: "MLB Terms & Conditions",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          "_self"
        );
      },
    },
  ];

  return (
    <>
      {links.map((link) => {
        return (
          <Stack
            key={link.id}
            spacing={2}
            direction={"row"}
            alignItems={"center"}
            onClick={() => link.onClick()}
            p={1}
            sx={{
              cursor: "pointer",
              pointerEvents: "auto",
              borderRadius: 2,
              "& .MuiSvgIcon-root": {
                color: theme.colors.gray[200],
              },
              "&:hover": {
                backgroundColor: theme.colors.gray[100],
              },
            }}
          >
            <SSText color={theme.colors.gray[300]}>{link.label}</SSText>
          </Stack>
        );
      })}
    </>
  );
};

export const FooterLinks = () => {
  const theme = useSSTheme();

  return (
    <>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            `${window.location.origin}/azdbacks_pass/dbacks_terms_and_condition`
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        Arizona Diamondbacks Terms and Conditions
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/privacy-policy"
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Privacy Policy
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/terms-of-use"
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Terms & Conditions
      </SSText>
    </>
  );
};
